// ROOM 404: Student Membership Card, Dynamic Rotating QR Pass & Profile Editor
// Pure alias representation: Real identity strictly concealed behind encrypted perimeter.

import React, { useEffect, useState } from 'react';
import {
  Award,
  Clock,
  Edit3,
  Info,
  Lock,
  RefreshCw,
  Save,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { QrCodeRenderer } from '../components/QrCodeRenderer';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/api';

interface CardData {
  alias: string;
  memberNumber: number;
  avatarUrl: string;
  cohort: string;
  standing: string;
  badgeName: string;
  badgeIcon: string;
  totalPoints: number;
}

interface QrData {
  token: string;
  expiresAt: number;
  ttlSeconds: number;
}

export const IdentityCard: React.FC = () => {
  const { session, logout, refreshSession } = useAuth();
  const [card, setCard] = useState<CardData | null>(null);
  const [qr, setQr] = useState<QrData | null>(null);
  const [loading, setLoading] = useState(true);

  // Edit Profile Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAlias, setEditAlias] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editInterests, setEditInterests] = useState<string[]>([]);
  const [newInterestInput, setNewInterestInput] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchCardData = async () => {
    try {
      const data = await apiRequest<{ card: CardData; qr: QrData }>('/api/identity/card');
      setCard(data.card);
      setQr(data.qr);
    } catch {
      // Ignored
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  const openEditModal = () => {
    setEditAlias(session?.profile?.alias || card?.alias || '');
    setEditBio(session?.profile?.biography || '');
    setEditInterests(session?.profile?.interests || ['tech', 'privacy']);
    setSaveError(null);
    setShowEditModal(true);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveLoading(true);
    setSaveError(null);

    try {
      await apiRequest('/api/identity/profile', {
        method: 'PUT',
        body: {
          alias: editAlias.trim() || undefined,
          biography: editBio.trim() || undefined,
          interests: editInterests,
        },
      });

      await refreshSession();
      await fetchCardData();
      setShowEditModal(false);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update profile');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAddInterest = () => {
    const val = newInterestInput.trim().toLowerCase();
    if (val && !editInterests.includes(val) && editInterests.length < 10) {
      setEditInterests([...editInterests, val]);
      setNewInterestInput('');
    }
  };

  const handleRemoveInterest = (interest: string) => {
    setEditInterests(editInterests.filter((i) => i !== interest));
  };

  const isRestricted = card?.standing === 'restricted' || session?.status === 'restricted';
  const isProbation = card?.standing === 'probation';

  return (
    <div className="space-y-4 pb-24 max-w-md mx-auto px-4 pt-3">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white tracking-tight">Identity Pass</h2>
          <p className="text-xs text-gray-400">Public alias token & dynamic entry credentials</p>
        </div>
        <button
          onClick={openEditModal}
          className="px-3 py-1.5 rounded-xl bg-[#121520] hover:bg-[#1a1f30] text-emerald-400 border border-emerald-500/30 text-xs font-semibold flex items-center space-x-1.5 transition-colors"
        >
          <Edit3 className="w-3.5 h-3.5" />
          <span>Edit Profile</span>
        </button>
      </div>

      {/* Futuristic Identity Card */}
      <div className="relative p-5 rounded-3xl bg-gradient-to-br from-[#121520] via-[#161a29] to-[#0d101a] border border-[#23293d] shadow-2xl overflow-hidden space-y-4">
        {/* Ambient background glow */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Card Header */}
        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center space-x-2">
            <span className="font-mono text-sm font-extrabold tracking-wider text-white">
              ROOM <span className="text-emerald-400">404</span>
            </span>
            <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              VERIFIED
            </span>
          </div>

          <span className="text-xs text-gray-400 font-mono tracking-widest">
            #{card?.memberNumber ? String(card.memberNumber).padStart(4, '0') : '0101'}
          </span>
        </div>

        {/* Member Profile Block */}
        <div className="flex items-center space-x-3.5 relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-500 to-cyan-500 p-0.5 shadow-lg">
            <div className="w-full h-full bg-[#0c0e17] rounded-[14px] flex items-center justify-center text-emerald-400 font-mono font-bold text-xl">
              {card?.alias ? card.alias.slice(0, 2).toUpperCase() : '404'}
            </div>
          </div>

          <div className="space-y-0.5">
            <h3 className="text-base font-bold text-white tracking-tight">{card?.alias || 'Initiate'}</h3>
            <div className="flex items-center space-x-2 text-xs text-gray-400 font-mono">
              <span className="text-cyan-400">{card?.cohort || 'Cohort 404'}</span>
              <span>•</span>
              <span className="text-amber-400 flex items-center space-x-0.5">
                <Sparkles className="w-3 h-3" />
                <span>{card?.totalPoints || 0} pts</span>
              </span>
            </div>
          </div>
        </div>

        {/* Badge & Standing Row */}
        <div className="flex items-center justify-between text-xs py-2 px-3 rounded-xl bg-[#090a0f]/60 border border-[#1f2433] relative z-10 font-mono">
          <div className="flex items-center space-x-1.5 text-indigo-300">
            <Award className="w-3.5 h-3.5 text-indigo-400" />
            <span>{card?.badgeName || 'Cohort 404'}</span>
          </div>

          <div
            className={`flex items-center space-x-1 font-semibold ${
              isRestricted ? 'text-rose-400' : isProbation ? 'text-amber-400' : 'text-emerald-400'
            }`}
          >
            {isRestricted ? (
              <>
                <ShieldAlert className="w-3.5 h-3.5" />
                <span>Restricted</span>
              </>
            ) : isProbation ? (
              <>
                <Shield className="w-3.5 h-3.5" />
                <span>Probation</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Good Standing</span>
              </>
            )}
          </div>
        </div>

        {/* Dynamic Rotating QR Section */}
        <div className="pt-2 relative z-10">
          {qr ? (
            <QrCodeRenderer
              token={qr.token}
              expiresAt={qr.expiresAt}
              ttlSeconds={qr.ttlSeconds}
              onRefresh={fetchCardData}
            />
          ) : (
            <div className="py-12 text-center text-gray-500 text-xs">Generating rotating QR token...</div>
          )}
        </div>
      </div>

      {/* Mandatory Student Disclosure */}
      <div className="p-3.5 rounded-2xl bg-[#12141c] border border-[#1f2433] text-[11px] text-gray-400 space-y-1.5 leading-relaxed">
        <div className="flex items-center space-x-1.5 text-emerald-400 font-semibold text-xs">
          <Info className="w-4 h-4" />
          <span>Identity Separation Disclosure</span>
        </div>
        <p>
          “Your alias is shown to other members. Your real identity is collected only for student verification, safety and accountability. Your student e-ID is permanently deleted immediately after verification. Your verified identity is encrypted and accessible only through a restricted, audited process.”
        </p>
      </div>

      {/* Sign Out Button */}
      <div className="pt-2">
        <button
          onClick={logout}
          className="w-full py-2.5 px-4 rounded-xl bg-[#12141c] hover:bg-red-500/10 text-gray-400 hover:text-red-400 border border-[#1f2433] text-xs font-medium transition-colors"
        >
          Sign Out of ROOM 404
        </button>
      </div>

      {/* Profile Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-[#121520] border border-[#23293d] rounded-2xl p-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#23293d] pb-3">
              <h3 className="text-sm font-bold text-white">Edit Member Profile</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {saveError && (
              <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs">
                {saveError}
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="space-y-3 text-xs">
              <div>
                <label className="block text-gray-300 font-semibold mb-1">Public Alias</label>
                <input
                  type="text"
                  value={editAlias}
                  onChange={(e) => setEditAlias(e.target.value)}
                  className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white font-mono focus:border-emerald-500 outline-none"
                  placeholder="Your alias"
                  maxLength={24}
                />
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-1">Biography</label>
                <textarea
                  value={editBio}
                  onChange={(e) => setEditBio(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white focus:border-emerald-500 outline-none resize-none"
                  placeholder="Brief student background..."
                  maxLength={280}
                />
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-1">Interests</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {editInterests.map((interest) => (
                    <span
                      key={interest}
                      className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[11px] flex items-center space-x-1"
                    >
                      <span>{interest}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveInterest(interest)}
                        className="hover:text-white"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex space-x-1.5">
                  <input
                    type="text"
                    value={newInterestInput}
                    onChange={(e) => setNewInterestInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddInterest();
                      }
                    }}
                    placeholder="Add interest tag..."
                    className="flex-1 px-2.5 py-1.5 bg-[#090a0f] border border-[#23293d] rounded-lg text-white outline-none text-xs"
                  />
                  <button
                    type="button"
                    onClick={handleAddInterest}
                    className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/30 font-semibold"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-3 py-1.5 bg-[#1a1f30] text-gray-300 rounded-lg hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveLoading}
                  className="px-4 py-1.5 bg-emerald-500 text-black font-semibold rounded-lg hover:bg-emerald-400 flex items-center space-x-1"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>{saveLoading ? 'Saving...' : 'Save'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
